import React from "react"
import { Plus, Trash2 } from "lucide-react"
import { Input } from "src/components/ui/input"
import { Textarea } from "src/components/ui/textarea"
import { Button } from "src/components/ui/button"
import { Checkbox } from "src/components/ui/checkbox"
import { RATIOS, HEADING_LEVELS, SPACER_SIZES } from "./blockSchema"
import { ImagePickerField } from "./ImagePicker"

// A small labelled field wrapper.
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-slate-600">{label}</span>
      {children}
    </label>
  )
}

function Segmented({ value, options, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
      {options.map((opt) => (
        <Button
          key={opt}
          type="button"
          variant="ghost"
          onClick={() => onChange(opt)}
          className={`h-auto rounded-md px-2.5 py-1 text-[12px] font-normal ${
            String(value) === String(opt) ? "bg-slate-900 text-white hover:bg-slate-800 hover:text-white" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          {opt}
        </Button>
      ))}
    </div>
  )
}

function HeadingInspector({ props, set }) {
  return (
    <div className="space-y-3">
      <Field label="Text">
        <Input value={props.text || ""} onChange={(e) => set({ text: e.target.value })} />
      </Field>
      <Field label="Size">
        <Segmented value={props.level || 2} options={HEADING_LEVELS} onChange={(v) => set({ level: Number(v) })} />
      </Field>
    </div>
  )
}

function TextInspector({ props, set }) {
  return (
    <div className="space-y-1">
      <Field label="Text">
        <Textarea
          rows={8}
          value={props.html || ""}
          onChange={(e) => set({ html: e.target.value })}
          placeholder={"Type your paragraphs here.\n\nPress Enter for a new line, and leave a blank line between paragraphs."}
          className="text-[13px]"
        />
      </Field>
      <p className="text-[12px] text-slate-500">
        Line breaks are kept. Blank line = new paragraph. For long content, prefer
        a Heading block above each section instead of one big text block.
      </p>
    </div>
  )
}

function ImageInspector({ props, set, images }) {
  return (
    <div className="space-y-3">
      <Field label="Image">
        <ImagePickerField multiple={false} images={images} value={props.image_id} onChange={(id) => set({ image_id: id })} />
      </Field>
      <Field label="Ratio">
        <Segmented value={props.ratio || "16:9"} options={RATIOS} onChange={(v) => set({ ratio: v })} />
      </Field>
    </div>
  )
}

function GalleryInspector({ props, set, images }) {
  return (
    <div className="space-y-3">
      <Field label="Images">
        <ImagePickerField multiple images={images} value={props.image_ids || []} onChange={(ids) => set({ image_ids: ids })} />
      </Field>
      <Field label="Ratio">
        <Segmented value={props.ratio || "16:9"} options={RATIOS} onChange={(v) => set({ ratio: v })} />
      </Field>
      <label className="flex items-center gap-2 text-[13px] text-slate-600">
        <Checkbox
          checked={!!props.show_posted_date}
          onCheckedChange={(v) => set({ show_posted_date: v === true })}
        />
        Show posted date overlay
      </label>
    </div>
  )
}

function SpecGridInspector({ props, set }) {
  const items = props.items || []
  const update = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it))
    set({ items: next })
  }
  const add = () => set({ items: [...items, { label: "", value: "" }] })
  const remove = (i) => set({ items: items.filter((_, idx) => idx !== i) })

  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            placeholder="Label"
            value={it.label || ""}
            onChange={(e) => update(i, "label", e.target.value)}
            className="flex-1"
          />
          <Input
            placeholder="Value"
            value={it.value || ""}
            onChange={(e) => update(i, "value", e.target.value)}
            className="flex-1"
          />
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} title="Remove">
            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add}>
        <Plus className="h-3.5 w-3.5" /> Add spec
      </Button>
    </div>
  )
}

// Which allowlisted provider a URL maps to (mirrors MicrositeBlocks::EMBED_PROVIDERS).
// Returns null for hosts we won't embed — the inspector then warns the broker.
function detectEmbedProvider(url) {
  let host
  try {
    host = new URL(url).hostname.toLowerCase()
  } catch {
    return null
  }
  if (/(^|\.)youtube\.com$|(^|\.)youtu\.be$/.test(host)) return "YouTube"
  if (/(^|\.)vimeo\.com$/.test(host)) return "Vimeo"
  if (/(^|\.)google\.com$/.test(host)) return "Google Maps"
  return null
}

function VideoInspector({ props, set }) {
  const url = props.url || ""
  const provider = url ? detectEmbedProvider(url) : null
  return (
    <div className="space-y-3">
      <Field label="Video or map URL">
        <Input
          placeholder="Paste a YouTube, Vimeo, or Google Maps link"
          value={url}
          onChange={(e) => set({ url: e.target.value })}
        />
      </Field>
      {url && (
        provider ? (
          <p className="text-[12px] text-emerald-600">✓ {provider} embed detected.</p>
        ) : (
          <p className="text-[12px] text-amber-600">
            This host can’t be embedded — it’ll show as a link instead. Use a YouTube, Vimeo, or Google Maps URL to embed.
          </p>
        )
      )}
      <Field label="Caption (optional)">
        <Input value={props.caption || ""} onChange={(e) => set({ caption: e.target.value })} />
      </Field>
      <Field label="Ratio">
        <Segmented value={props.ratio || "16:9"} options={RATIOS} onChange={(v) => set({ ratio: v })} />
      </Field>
    </div>
  )
}

function SpacerInspector({ props, set }) {
  return (
    <Field label="Size">
      <Segmented value={props.size || "md"} options={SPACER_SIZES} onChange={(v) => set({ size: v })} />
    </Field>
  )
}

function TagsInspector({ props, set }) {
  const tags = props.tags || []
  const update = (i, val) => set({ tags: tags.map((t, idx) => (idx === i ? val : t)) })
  const add = () => set({ tags: [...tags, ""] })
  const remove = (i) => set({ tags: tags.filter((_, idx) => idx !== i) })

  return (
    <div className="space-y-2">
      <p className="text-[12px] text-slate-500">e.g. NEW, 2BHK, 3BHK, NA Land, Commercial</p>
      {tags.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input placeholder="Tag" value={t || ""} onChange={(e) => update(i, e.target.value)} className="flex-1" />
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} title="Remove">
            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add}>
        <Plus className="h-3.5 w-3.5" /> Add tag
      </Button>
    </div>
  )
}

function FeatureGridInspector({ props, set }) {
  const items = props.items || []
  const update = (i, key, val) => set({ items: items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)) })
  const add = () => set({ items: [...items, { icon: "", title: "", text: "" }] })
  const remove = (i) => set({ items: items.filter((_, idx) => idx !== i) })

  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="space-y-2 rounded-lg border border-slate-200 p-2.5">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Icon"
              value={it.icon || ""}
              onChange={(e) => update(i, "icon", e.target.value)}
              className="w-16 text-center"
            />
            <Input
              placeholder="Title"
              value={it.title || ""}
              onChange={(e) => update(i, "title", e.target.value)}
              className="flex-1"
            />
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} title="Remove">
              <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
            </Button>
          </div>
          <Textarea
            rows={2}
            placeholder="Description"
            value={it.text || ""}
            onChange={(e) => update(i, "text", e.target.value)}
            className="text-[13px]"
          />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add}>
        <Plus className="h-3.5 w-3.5" /> Add feature
      </Button>
    </div>
  )
}

function AmenitiesInspector({ props, set }) {
  const groups = props.groups || []
  const setGroup = (gi, patch) => set({ groups: groups.map((g, idx) => (idx === gi ? { ...g, ...patch } : g)) })
  const addGroup = () => set({ groups: [...groups, { name: "", items: [] }] })
  const removeGroup = (gi) => set({ groups: groups.filter((_, idx) => idx !== gi) })

  const setItem = (gi, ii, val) => {
    const g = groups[gi]
    setGroup(gi, { items: (g.items || []).map((it, idx) => (idx === ii ? val : it)) })
  }
  const addItem = (gi) => setGroup(gi, { items: [...(groups[gi].items || []), ""] })
  const removeItem = (gi, ii) => setGroup(gi, { items: (groups[gi].items || []).filter((_, idx) => idx !== ii) })

  return (
    <div className="space-y-3">
      {groups.map((g, gi) => (
        <div key={gi} className="space-y-2 rounded-lg border border-slate-200 p-2.5">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Group (e.g. Fitness & Wellness)"
              value={g.name || ""}
              onChange={(e) => setGroup(gi, { name: e.target.value })}
              className="flex-1 font-medium"
            />
            <Button type="button" variant="ghost" size="icon" onClick={() => removeGroup(gi)} title="Remove group">
              <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
            </Button>
          </div>
          <div className="space-y-1.5 pl-1">
            {(g.items || []).map((it, ii) => (
              <div key={ii} className="flex items-center gap-2">
                <Input
                  placeholder="Amenity (e.g. Swimming Pool)"
                  value={it || ""}
                  onChange={(e) => setItem(gi, ii, e.target.value)}
                  className="flex-1"
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(gi, ii)} title="Remove">
                  <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="ghost" size="sm" className="gap-1 text-slate-500" onClick={() => addItem(gi)}>
              <Plus className="h-3.5 w-3.5" /> Add amenity
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addGroup}>
        <Plus className="h-3.5 w-3.5" /> Add group
      </Button>
    </div>
  )
}

const INSPECTORS = {
  heading: HeadingInspector,
  text: TextInspector,
  image: ImageInspector,
  image_gallery: GalleryInspector,
  video_embed: VideoInspector,
  spec_grid: SpecGridInspector,
  tags: TagsInspector,
  feature_grid: FeatureGridInspector,
  amenities: AmenitiesInspector,
  spacer: SpacerInspector,
}

// Renders the prop editor for the selected block. `images` = the project's
// available images for picker blocks. `onChange(nextProps)` is called with the
// merged props.
export function BlockInspector({ block, images, onChange }) {
  if (!block) {
    return <p className="text-[13px] text-slate-400">Select a block to edit it, or add one from the palette.</p>
  }
  const Comp = INSPECTORS[block.type]
  if (!Comp) return <p className="text-[13px] text-slate-400">This block has no settings.</p>

  const set = (patch) => onChange({ ...block.props, ...patch })
  return <Comp props={block.props || {}} set={set} images={images} />
}
